import React, { useEffect, useMemo, useState } from "react";
import {
  getCatalogueImages,
  addCatalogueImages,
  updateCatalogueImage,
  deleteCatalogueImage,
  setCatalogueSortOrder,
  setProductKey,
  groupIntoProducts,
  orderWithGrouped,
  orderWithCover,
  orderWithProductMoved,
} from "../services/catalogueService";
import { uploadFile } from "../services/storage";
import { isSupabaseConfigured } from "../supabase/client";
import { CATEGORIES } from "../data/categories";

const CatalogueAdmin = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(CATEGORIES[0].slug);
  const [files, setFiles] = useState([]);
  const [caption, setCaption] = useState("");
  const [asOneProduct, setAsOneProduct] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]); // image ids ticked for grouping

  const load = async () => {
    setLoading(true);
    try {
      setImages(await getCatalogueImages());
      setError("");
    } catch (e) {
      setError("Couldn't load the catalogue. Has supabase/catalogue.sql been run?");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isSupabaseConfigured) load();
    else setLoading(false);
  }, []);

  // Selection is per category, so switching tabs clears it rather than leaving
  // ticks on rows that are no longer visible.
  useEffect(() => { setSelected([]); }, [category]);

  // Count per category so the tabs show where images already exist.
  const counts = useMemo(() => {
    const c = {};
    for (const img of images) c[img.category] = (c[img.category] || 0) + 1;
    return c;
  }, [images]);

  const shown = useMemo(
    () => images.filter((i) => i.category === category),
    [images, category]
  );

  // What the public catalogue will show: one entry per product.
  const products = useMemo(() => groupIntoProducts(shown), [shown]);

  // Grouping needs the product_key column. Until that migration is run every
  // call fails on a missing column, so say which file to run rather than
  // surfacing the raw Postgres error.
  const explain = (e, fallback) => {
    const msg = e?.message || "";
    if (/product_key|column .* does not exist|schema cache/i.test(msg)) {
      return "Grouping needs one more migration — run supabase/catalogue-products.sql in the Supabase SQL editor, then reload.";
    }
    return msg || fallback;
  };

  const toggleSelect = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  /** Renumber the whole category so each product's views stay adjacent. */
  const renumber = async (ordered) => {
    await setCatalogueSortOrder(ordered);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!files.length) return;
    setUploading(true);
    setError("");

    try {
      // One key for the whole batch when these are views of a single product.
      const groupKey =
        asOneProduct && files.length > 1
          ? (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()))
          : null;

      // Continue after a single bad file rather than losing the whole batch.
      const rows = [];
      const failed = [];
      let nextOrder = shown.reduce((m, i) => Math.max(m, i.sortOrder || 0), 0);

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProgress(`Uploading ${i + 1} of ${files.length}…`);
        try {
          const { url } = await uploadFile(file, `catalogue/${category}`);
          nextOrder += 1;
          rows.push({
            category,
            imageUrl: url,
            caption: files.length === 1 ? caption : caption || null,
            sortOrder: nextOrder,
            active: true,
            productKey: groupKey,
          });
        } catch (err) {
          failed.push(file.name);
        }
      }

      if (rows.length) await addCatalogueImages(rows);
      if (failed.length) setError(`Couldn't upload: ${failed.join(", ")}`);

      setFiles([]);
      setCaption("");
      setAsOneProduct(false);
      setProgress("");
      await load();
    } catch (err) {
      setError(explain(err, "Upload failed."));
    }
    setUploading(false);
    setProgress("");
  };

  const toggleActive = async (img) => {
    await updateCatalogueImage(img.id, { active: !(img.active !== false) });
    await load();
  };

  const handleDelete = async (img) => {
    if (!window.confirm("Remove this image from the catalogue?")) return;
    await deleteCatalogueImage(img.id);
    await load();
  };

  const saveCaption = async (img, value) => {
    if ((img.caption || "") === value) return;
    await updateCatalogueImage(img.id, { caption: value || null });
    await load();
  };

  /** Tick several images, then combine them into one product. */
  const groupSelected = async () => {
    if (selected.length < 2) return;
    setBusy(true);
    try {
      const key = crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
      await setProductKey(selected, key);

      // Move the grouped views together, at the position of the first of them,
      // so the product reads as one block in the public grid.
      await renumber(orderWithGrouped(shown, selected));

      setSelected([]);
      await load();
    } catch (e) {
      setError(explain(e, "Couldn't group those images."));
    }
    setBusy(false);
  };

  /** Split a product back into separate single-image products. */
  const ungroup = async (product) => {
    setBusy(true);
    try {
      await setProductKey(product.images.map((i) => i.id), null);
      await load();
    } catch (e) {
      setError(explain(e, "Couldn't ungroup that product."));
    }
    setBusy(false);
  };

  /** Promote one view to be the tile the public grid shows. */
  const makeCover = async (product, img) => {
    setBusy(true);
    try {
      await renumber(orderWithCover(shown, product, img.id));
      await load();
    } catch (e) {
      setError(explain(e, "Couldn't change the cover."));
    }
    setBusy(false);
  };

  /** Move a whole product up or down among the other products. */
  const moveProduct = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= products.length) return;
    setBusy(true);
    try {
      await renumber(orderWithProductMoved(products, index, dir));
      await load();
    } catch (e) {
      setError(explain(e, "Couldn't reorder."));
    }
    setBusy(false);
  };

  const catTitle = CATEGORIES.find((c) => c.slug === category).title;

  return (
    <>
      <div className="admin-topbar">
        <h2>Product Catalogue</h2>
      </div>

      <div className="admin-content">
        {!isSupabaseConfigured && (
          <div className="config-warning">
            Add your Supabase keys to <code>.env</code> to enable editing.
          </div>
        )}
        {error && <div className="admin-alert error">{error}</div>}

        {/* Category tabs */}
        <div className="cat-tabs">
          {CATEGORIES.map((c) => (
            <button
              key={c.slug}
              className={`cat-tab ${category === c.slug ? "on" : ""}`}
              onClick={() => setCategory(c.slug)}
            >
              {c.title}
              <span className="cat-tab-count">{counts[c.slug] || 0}</span>
            </button>
          ))}
        </div>

        {/* Uploader */}
        <div className="admin-panel">
          <div className="admin-panel-head">
            <h3>Add images to {catTitle}</h3>
          </div>

          <form className="cat-upload" onSubmit={handleUpload}>
            <div className="admin-field">
              <label>Images</label>
              <input
                type="file"
                accept="image/*"
                multiple
                disabled={!isSupabaseConfigured || uploading}
                onChange={(e) => setFiles([...e.target.files])}
              />
              <p className="hint">
                You can select several at once. Portrait product shots work best —
                the catalogue shows them in a grid.
              </p>
            </div>

            <div className="admin-field">
              <label className="cat-check">
                <input
                  type="checkbox"
                  checked={asOneProduct}
                  disabled={!isSupabaseConfigured || uploading || files.length < 2}
                  onChange={(e) => setAsOneProduct(e.target.checked)}
                />
                <span>
                  These are all views of <strong>one product</strong>
                  {files.length > 1 && ` (${files.length} images)`}
                </span>
              </label>
              <p className="hint">
                Tick this for the front, back and detail shots of the same
                garment. The catalogue then shows one tile, and the other views
                appear when a buyer opens it. Leave it unticked to add each image
                as a separate product. The first image you select becomes the tile.
              </p>
            </div>

            <div className="admin-field">
              <label>Caption {files.length > 1 && !asOneProduct && "(applied to all)"}</label>
              <input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Optional — e.g. Five-pocket straight leg, 12oz denim"
                disabled={!isSupabaseConfigured || uploading}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isSupabaseConfigured || uploading || !files.length}
            >
              {uploading
                ? progress || "Uploading…"
                : asOneProduct && files.length > 1
                ? `Upload as 1 product (${files.length} views)`
                : `Upload ${files.length || ""} image${files.length === 1 ? "" : "s"}`}
            </button>
          </form>
        </div>

        {/* Existing products for this category */}
        <div className="admin-panel">
          <div className="admin-panel-head">
            <h3>
              {products.length} product{products.length === 1 ? "" : "s"} ·{" "}
              {shown.length} image{shown.length === 1 ? "" : "s"}
            </h3>
            {selected.length > 0 && (
              <div className="cat-bulk">
                <span>{selected.length} selected</span>
                <button
                  className="btn btn-primary"
                  onClick={groupSelected}
                  disabled={selected.length < 2 || busy}
                >
                  Group as one product
                </button>
                <button className="btn btn-ghost" onClick={() => setSelected([])}>
                  Clear
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="admin-empty">Loading…</div>
          ) : products.length === 0 ? (
            <div className="admin-empty">
              Nothing here yet. Until you add images, this category shows a
              “catalogue coming soon” message on the website.
            </div>
          ) : (
            <ul className="cat-products">
              {products.map((p, pi) => {
                const grouped = p.images.length > 1;
                return (
                  <li className={`cat-product ${grouped ? "grouped" : ""}`} key={p.key}>
                    <div className="cat-product-bar">
                      <span className="cat-product-label">
                        {grouped ? `Product · ${p.images.length} views` : "Single image"}
                      </span>
                      <div className="cat-product-tools">
                        <button
                          className="btn btn-ghost"
                          onClick={() => moveProduct(pi, -1)}
                          disabled={pi === 0 || busy}
                          title="Move product up"
                        >
                          ↑
                        </button>
                        <button
                          className="btn btn-ghost"
                          onClick={() => moveProduct(pi, 1)}
                          disabled={pi === products.length - 1 || busy}
                          title="Move product down"
                        >
                          ↓
                        </button>
                        {grouped && (
                          <button
                            className="btn btn-ghost"
                            onClick={() => ungroup(p)}
                            disabled={busy}
                          >
                            Ungroup
                          </button>
                        )}
                      </div>
                    </div>

                    <ul className="cat-grid">
                      {p.images.map((img, ii) => (
                        <li
                          className={`cat-item ${img.active === false ? "off" : ""} ${
                            selected.includes(img.id) ? "picked" : ""
                          }`}
                          key={img.id}
                        >
                          <div className="cat-thumb">
                            <img src={img.imageUrl} alt={img.caption || ""} loading="lazy" />
                            {img.active === false && <span className="cat-hidden">Hidden</span>}
                            {grouped && ii === 0 && <span className="cat-cover-flag">Tile</span>}
                            <label className="cat-pick" title="Select for grouping">
                              <input
                                type="checkbox"
                                checked={selected.includes(img.id)}
                                onChange={() => toggleSelect(img.id)}
                              />
                            </label>
                          </div>

                          <input
                            className="cat-caption"
                            defaultValue={img.caption || ""}
                            placeholder="Add a caption…"
                            onBlur={(e) => saveCaption(img, e.target.value.trim())}
                          />

                          <div className="cat-actions">
                            {grouped && ii !== 0 && (
                              <button
                                className="btn btn-ghost"
                                onClick={() => makeCover(p, img)}
                                disabled={busy}
                              >
                                Make tile
                              </button>
                            )}
                            <button className="btn btn-ghost" onClick={() => toggleActive(img)}>
                              {img.active === false ? "Show" : "Hide"}
                            </button>
                            <button className="btn btn-danger-text" onClick={() => handleDelete(img)}>
                              Delete
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )}

          {products.length > 0 && (
            <p className="hint cat-group-hint">
              To combine images that are already uploaded, tick them and choose
              “Group as one product”.
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default CatalogueAdmin;
